import type { Ref } from "react";
import { cn } from "@/lib/cn";
import type { InputTargetRowIconSource } from "./input-target-row-icon";
import { StandaloneBarGhostRoute } from "./standalone-bar-ghost-route";

/** Same tokens as the question options' keycaps; 10px via the AppKit mini size. */
const KEYCAP_CLASS =
  "ml-1.5 shrink-0 rounded-[5px] border border-glass-stroke-subtle px-1 text-appkit-mini leading-[15px] text-muted";

/** Wrap the full prediction inline; it contributes to the composer's height. */
function GhostText({ text }: { text: string }) {
  return (
    <>
      <span data-standalone-ghost-text className="whitespace-normal [overflow-wrap:anywhere] text-muted">{text}</span>
      <kbd className={KEYCAP_CLASS}>tab</kbd>
    </>
  );
}

/** Empty bar, routed chat with a prediction: the chat as a chip, the prediction, `tab`. */
export function StandaloneBarPlaceholderGhost({ route, icon, text, onSelect, disabled }: {
  route: string;
  icon: InputTargetRowIconSource;
  text: string;
  onSelect: () => void;
  disabled: boolean;
}) {
  return (
    <span
      onPointerDown={(event) => { if (!disabled) { event.preventDefault(); onSelect(); } }}
      data-standalone-ghost="placeholder"
      data-testid="standalone-bar-ghost"
      className="z-[1] block w-fit min-w-0 max-w-full self-start cursor-text whitespace-normal py-1 text-appkit-body font-normal leading-5 text-muted [grid-area:1/1]"
    >
      <StandaloneBarGhostRoute route={route} icon={icon} />
      <GhostText text={text} />
    </span>
  );
}

/** A picked chip and nothing typed: the prediction where typed text would start.
 *  `placeChipGhost` positions it from the chip, outside the editable root.
 *  Without room (`shown` false) it stays laid out but invisible, so it can be
 *  placed and measured again as the bar grows. */
export function StandaloneBarChipGhost({ ref, text, shown, onSelect, disabled }: {
  ref: Ref<HTMLDivElement>;
  text: string;
  shown: boolean;
  onSelect: () => void;
  disabled: boolean;
}) {
  return (
    <div
      ref={ref}
      data-standalone-ghost="after-chip"
      data-testid="standalone-bar-ghost"
      className={cn(
        "absolute left-0 top-0 z-[1] min-w-0 whitespace-normal text-appkit-body leading-5",
        !shown && "invisible",
      )}
    >
      <span
        data-testid="standalone-bar-chip-ghost-select"
        onPointerDown={(event) => { if (!disabled) { event.preventDefault(); onSelect(); } }}
        className="block w-fit min-w-0 max-w-full cursor-text whitespace-normal text-left font-normal leading-5 text-muted">
        <GhostText text={text} />
      </span>
    </div>
  );
}
